import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Loader2, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const nyDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const label = (s) => (s || '—').replace(/_/g, ' ');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

// Page → CTA: which button on this page produced its leads and conversions.
const CtaBreakdown = ({ rows }) => (
  <div className="space-y-2" data-testid="drill-cta-breakdown">
    <p className="text-sm font-semibold text-neutral-900">By button (CTA)</p>
    {!rows.length ? <p className="text-xs text-neutral-500">No leads from this page this month.</p> : (
      <div className="border border-neutral-200 rounded-lg divide-y">
        {rows.map((c, i) => (
          <div key={c.cta} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-3 py-2" data-testid={`drill-cta-${i}`}>
            <p className={`text-sm ${c.recorded ? 'font-medium text-neutral-900' : 'italic text-neutral-500'}`} data-testid={`drill-cta-${i}-name`}>{c.cta}</p>
            <p className="text-sm text-neutral-700" data-testid={`drill-cta-${i}-summary`}>
              {plural(c.total_leads, 'lead')} · <strong>{c.converted} converted</strong>{c.conversion_pct != null && <span className="text-neutral-500"> · {c.conversion_pct}%</span>}
              <span className="text-xs text-neutral-500"> · {plural(c.consultation_conversions, 'consultation conversion')} · {plural(c.quote_conversions, 'quote conversion')}</span>
              {c.quote_not_trackable > 0 && <span className="text-[11px] text-neutral-400"> · {c.quote_not_trackable} not trackable</span>}
            </p>
          </div>
        ))}
      </div>
    )}
    <p className="text-[11px] text-neutral-400">Most converted first, then conversion rate, then leads. Rate = converted ÷ (consultations + trackable quote requests).</p>
  </div>
);

const Ext = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={testId}>
    <ExternalLink className="w-3 h-3" />{children}
  </a>
);

const Converted = ({ yes, text }) => (yes
  ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><CheckCircle2 className="w-3.5 h-3.5" />Converted</span>
  : <span className="text-xs text-neutral-500">{text || 'Not converted'}</span>);

const Row = ({ cells, testId }) => (
  <tr className="border-t border-neutral-100 align-top" data-testid={testId}>{cells.map((c, i) => <td key={i} className="px-3 py-2">{c}</td>)}</tr>
);

const Section = ({ title, head, rows, empty, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <p className="text-sm font-semibold text-neutral-900">{title} <span className="font-normal text-neutral-400">({rows.length})</span></p>
    {rows.length ? (
      <div className="border border-neutral-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-neutral-50 text-xs text-neutral-500 text-left"><tr>{head.map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr></thead><tbody>{rows}</tbody></table>
      </div>
    ) : <p className="text-xs text-neutral-500">{empty}</p>}
  </div>
);

// Read-only list of the leads behind one Top Lead Pages card (same month and conversion rules as the report).
export const LeadPageDrilldown = ({ token, month, page, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!page) return;
    setD(null);
    axios.get(`${API_URL}/admin/lead-report/page-leads`, { headers: { Authorization: `Bearer ${token}` }, params: { month, path: page.path } })
      .then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [page, month, token]);
  const cons = (d?.consultations || []).map((c) => (
    <Row key={c.id} testId={`drill-consultation-${c.id}`} cells={[
      <><p className="font-medium">{c.customer_name || '—'}</p><p className="text-xs text-neutral-400">{c.email}</p></>,
      nyDate(c.created_at), <span className="capitalize">{label(c.type)}</span>, c.cta || '—', <span className="capitalize">{label(c.status)}</span>,
      <Converted yes={c.converted} />, <Ext href={c.link} testId={`drill-consultation-link-${c.id}`}>Open consultation</Ext>]} />
  ));
  const quotes = (d?.quote_requests || []).map((q) => (
    <Row key={q.id} testId={`drill-quote-${q.id}`} cells={[
      <><p className="font-medium">{q.customer_name || '—'}</p><p className="text-xs text-neutral-400">{q.email}</p></>,
      nyDate(q.created_at), <>{q.channel_label}<p className="text-xs text-neutral-400 font-mono">{q.reference}</p></>, q.cta || '—', <span className="capitalize">{label(q.status)}</span>,
      <><Converted yes={q.converted} text={q.trackable ? 'Not converted' : 'Not trackable'} />{q.trackable && !q.converted && <p className="text-[11px] text-neutral-400">trackable</p>}</>,
      <div className="space-y-1">
        {q.linked_quote && <p className="text-xs"><span className="font-mono">#{q.linked_quote.number}</span> <span className="capitalize text-neutral-500">{label(q.linked_quote.status)}</span></p>}
        {q.link && <div><Ext href={q.link} testId={`drill-quote-link-${q.id}`}>Open request</Ext></div>}
        {q.quote_link && <div><Ext href={q.quote_link} testId={`drill-quote-sales-link-${q.id}`}>Open Sales Quote</Ext></div>}
      </div>]} />
  ));
  return (
    <Dialog open={!!page} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)]" data-testid="lead-page-drilldown">
        <DialogHeader>
          <DialogTitle>{page?.title} <span className="text-sm font-normal text-neutral-400">{page?.path}</span></DialogTitle>
          <DialogDescription>{d?.label || ''} · New York dates · {page?.total_leads} leads, {page?.converted} converted. Links open in a new tab.</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
          : d.error ? <p className="text-sm text-red-700">Could not load the leads for this page.</p> : (
            <div className="space-y-6">
              <CtaBreakdown rows={d.ctas || []} />
              <Section title="Consultations" testId="drill-consultations" rows={cons} empty="No consultations from this page this month."
                head={['Customer', 'Submitted', 'Type', 'Button clicked', 'Status', 'Converted', '']} />
              <Section title="Quote Requests" testId="drill-quotes" rows={quotes} empty="No quote requests from this page this month."
                head={['Customer', 'Submitted', 'Channel', 'Button clicked', 'Status', 'Conversion', 'Sales Quote / links']} />
              <p className="text-[11px] text-neutral-400">Consultations convert when Confirmed or Completed. Quote requests convert when their Sales Quote is approved, accepted or signed, or a Sales Order is created. Not-trackable requests have no Sales Quote yet.</p>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
};
